import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Switch } from './ui/switch';
import { Textarea } from './ui/textarea';

interface JiraConfig {
  projectKey: string;
  jiraUrl: string;
  username: string;
  apiToken: string;
}

interface CreateProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateProject: (projectData: {
    name: string;
    description?: string;
    jiraEnabled?: boolean;
    jiraConfig?: JiraConfig;
  }) => void;
}

export const CreateProjectModal: React.FC<CreateProjectModalProps> = ({
  isOpen,
  onClose,
  onCreateProject
}) => {
  const [projectName, setProjectName] = useState('');
  const [description, setDescription] = useState('');
  const [jiraEnabled, setJiraEnabled] = useState(false);
  const [jiraConfig, setJiraConfig] = useState<JiraConfig>({
    projectKey: '',
    jiraUrl: '',
    username: '',
    apiToken: ''
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!projectName.trim()) return;

    // Validate JIRA config if enabled
    if (jiraEnabled) {
      const { projectKey, jiraUrl, username, apiToken } = jiraConfig;
      if (!projectKey.trim() || !jiraUrl.trim() || !username.trim() || !apiToken.trim()) {
        alert('Por favor, preencha todos os campos da configuração JIRA.');
        return;
      }
    }

    setIsLoading(true);
    try {
      const projectData = {
        name: projectName.trim(),
        description: description.trim() || undefined,
        jiraEnabled: jiraEnabled,
        jiraConfig: jiraEnabled ? jiraConfig : undefined
      };
      
      await onCreateProject(projectData);
      resetForm();
      onClose();
    } catch (error: any) {
      console.error('Error creating project:', error);
      
      // Show user-friendly error message
      const errorMessage = error?.response?.data?.message || 'Erro ao criar projeto. Tente novamente.';
      if (errorMessage.includes('JIRA') || jiraEnabled) {
        alert(`Erro ao criar projeto com configuração JIRA: ${errorMessage}`);
      } else {
        alert(`Erro ao criar projeto: ${errorMessage}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setProjectName('');
    setDescription('');
    setJiraEnabled(false);
    setJiraConfig({
      projectKey: '',
      jiraUrl: '',
      username: '',
      apiToken: ''
    });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const updateJiraConfig = (field: keyof JiraConfig, value: string) => {
    setJiraConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <Card variant="glass" className="w-full max-w-md bg-background-secondary border border-white/10">
        <CardHeader className="flex flex-row items-center justify-between pb-4">
          <CardTitle className="text-xl font-heading text-foreground-primary">
            Criar Novo Projeto
          </CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={handleClose}
            className="h-8 w-8 p-0 border-white/20 hover:bg-white/10"
          >
            <X className="w-4 h-4" />
          </Button>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="projectName" className="text-foreground-secondary">
                Nome do Projeto *
              </Label>
              <Input
                id="projectName"
                type="text"
                placeholder="Digite o nome do projeto"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                className="border-white/30 placeholder:text-muted-foreground focus:border-blue-400 focus:ring-2 focus:ring-blue-500/30"
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description" className="text-foreground-secondary">
                Descrição
              </Label>
              <Textarea
                id="description"
                placeholder="Descrição do projeto (opcional)"
                value={description}
                onChange={setDescription}
                rows={3}
                className="border-white/30 placeholder:text-muted-foreground focus:border-blue-400 focus:ring-2 focus:ring-blue-500/30"
              />
            </div>

            {/* JIRA Integration Section */}
            <div className="pt-2 border-t border-white/10">
              <div className="flex items-center justify-between mb-4">
                <div className="space-y-1">
                  <Label className="text-foreground-secondary font-medium">
                    Integração JIRA
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Configure a integração com JIRA para exportar tarefas automaticamente
                  </p>
                </div>
                <Switch
                  checked={jiraEnabled}
                  onCheckedChange={setJiraEnabled}
                />
              </div>

              {jiraEnabled && (
                <div className="space-y-4 bg-white/5 p-4 rounded-lg border border-white/10">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="projectKey" className="text-foreground-secondary text-sm">
                        Chave do Projeto *
                      </Label>
                      <Input
                        id="projectKey"
                        type="text"
                        placeholder="Ex: PROJ"
                        value={jiraConfig.projectKey}
                        onChange={(e) => updateJiraConfig('projectKey', e.target.value)}
                        className="border-white/30 placeholder:text-muted-foreground focus:border-blue-400 focus:ring-2 focus:ring-blue-500/30"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="jiraUrl" className="text-foreground-secondary text-sm">
                        URL do JIRA *
                      </Label>
                      <Input
                        id="jiraUrl"
                        type="url"
                        placeholder="https://empresa.atlassian.net"
                        value={jiraConfig.jiraUrl}
                        onChange={(e) => updateJiraConfig('jiraUrl', e.target.value)}
                        className="border-white/30 placeholder:text-muted-foreground focus:border-blue-400 focus:ring-2 focus:ring-blue-500/30"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="username" className="text-foreground-secondary text-sm">
                        Usuário *
                      </Label>
                      <Input
                        id="username"
                        type="text"
                        placeholder="usuario@empresa.com"
                        value={jiraConfig.username}
                        onChange={(e) => updateJiraConfig('username', e.target.value)}
                        className="border-white/30 placeholder:text-muted-foreground focus:border-blue-400 focus:ring-2 focus:ring-blue-500/30"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="apiToken" className="text-foreground-secondary text-sm">
                        API Token *
                      </Label>
                      <Input
                        id="apiToken"
                        type="password"
                        placeholder="API Token do JIRA"
                        value={jiraConfig.apiToken}
                        onChange={(e) => updateJiraConfig('apiToken', e.target.value)}
                        className="border-white/30 placeholder:text-muted-foreground focus:border-blue-400 focus:ring-2 focus:ring-blue-500/30"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    As configurações JIRA serão criptografadas e armazenadas com segurança.
                  </p>
                </div>
              )}
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                className="flex-1 border-white/20 hover:bg-white/10"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="gradient"
                loading={isLoading}
                loadingText="Criando..."
                disabled={!projectName.trim()}
                className="flex-1"
              >
                Criar Projeto
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};